import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '../components/ui/accordion';
import { Link } from 'react-router-dom';
import { 
  Users, Shield, Award, Globe, CheckCircle, ArrowRight, Star,
  Zap, Lock, Target, Briefcase, Calculator, Scale, Monitor, Server, Cloud, Brain, Smartphone, Key, Heart,
  Play, MapPin, Phone, Mail, Building2, Sparkles, TrendingUp, Clock, Check
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import SchemaMarkup, { generateFAQSchema, generateReviewSchema } from '../components/SchemaMarkup';
import { Toaster } from 'sonner';
import { useBooking } from '../context/BookingContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Animated Counter
const AnimatedCounter = ({ value, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    
    // Handle decimal values
    const hasDecimal = value.toString().includes('.');
    const numValue = parseFloat(value.toString().replace(/[^0-9.]/g, ''));
    if (isNaN(numValue)) return;
    
    let start = 0;
    const duration = 2000;
    const increment = numValue / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= numValue) {
        setCount(hasDecimal ? numValue.toFixed(1) : numValue);
        clearInterval(timer);
      } else {
        setCount(hasDecimal ? start.toFixed(1) : Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, value]);

  // Format large numbers
  const formatNumber = (num) => {
    if (typeof num === 'string') return num;
    if (num >= 1000000) return (num / 1000000).toFixed(0) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
    return num.toLocaleString();
  };

  return <span ref={ref}>{formatNumber(count)}{suffix}</span>;
};

const AboutPage = () => {
  const { openBooking, openContact } = useBooking();
  const [aboutSections, setAboutSections] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeValue, setActiveValue] = useState(0);

  const iconMap = {
    Monitor, Smartphone, Server, Brain, Cloud, Lock, Briefcase, Calculator, Scale, Users,
    Key, Target, Shield, Award, Zap, Globe, Heart
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [aboutRes, faqsRes, testimonialsRes] = await Promise.all([
          fetch(`${API_URL}/api/cms/about`),
          fetch(`${API_URL}/api/cms/faqs`),
          fetch(`${API_URL}/api/cms/testimonials`)
        ]);
        
        const aboutData = await aboutRes.json();
        const faqsData = await faqsRes.json();
        const testimonialsData = await testimonialsRes.json();
        
        setAboutSections(aboutData || []);
        setFaqs(faqsData || []);
        // Map CMS 'content' field to 'text' for display compatibility
        const mappedTestimonials = (testimonialsData || []).map(t => ({
          ...t,
          text: t.content || t.text
        }));
        setTestimonials(mappedTestimonials);
      } catch (e) {
        console.error('Failed to fetch About page data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-cycle values
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveValue((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Get specific sections
  const heroSection = aboutSections.find(s => s.section_type === 'hero');
  const storySection = aboutSections.find(s => s.section_type === 'story');
  const valuesSection = aboutSections.find(s => s.section_type === 'values');
  const differentiatorsSection = aboutSections.find(s => s.section_type === 'differentiators');
  const techStackSection = aboutSections.find(s => s.section_type === 'tech_stack');
  const partnersSection = aboutSections.find(s => s.section_type === 'partners');

  // Trust Stats
  const trustStats = [
    { value: '20000', suffix: '+', label: 'Participants Served', icon: Users },
    { value: '100', suffix: '+', label: 'Happy Providers', icon: Building2 },
    { value: '99.8', suffix: '%', label: 'Compliance Rate', icon: Shield },
    { value: '13', suffix: ' Years', label: 'In Business', icon: Clock }
  ];

  // Core Values
  const coreValues = [
    { icon: Heart, title: 'Care First', description: 'Every feature we build is designed to help you deliver better care to NDIS participants.' },
    { icon: Shield, title: 'Trust & Security', description: 'Your data stays yours. Australian-hosted, NDIS-compliant, and fully encrypted.' },
    { icon: Zap, title: 'Innovation', description: 'We leverage AI and modern technology to automate compliance and reduce admin burden.' },
    { icon: Users, title: 'Partnership', description: 'We work alongside you, not just for you. Your success is our success.' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-tech-blue/30 border-t-tech-blue rounded-full animate-spin"></div>
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page-bg">
      <SEOHead 
        title="About Us | GetCare 360 - 13 Years of NDIS Software Excellence"
        description="GetCare 360 has 13 years of experience building custom NDIS software for Australian disability care providers. Learn about our mission, team, and commitment to quality."
        keywords="about GetCare 360, NDIS software company, Australian disability software, NDIS provider solutions"
        canonicalUrl="/about"
        page="about"
      />
      {faqs.length > 0 && <SchemaMarkup schema={generateFAQSchema(faqs)} />}
      {testimonials.length > 0 && <SchemaMarkup schema={generateReviewSchema(testimonials)} />}
      <Toaster position="top-right" richColors />
      <Header />

      {/* Hero Section - Redesigned */}
      <section className="pt-32 pb-24 bg-gradient-to-br from-primary-navy via-[#0d2847] to-[#0a1f3d] text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-care-teal/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }}></div>
          <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-tech-blue/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl"></div>
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-care-teal/20 rounded-full border border-care-teal/30 mb-6">
                <Award className="text-care-teal mr-2" size={18} />
                <span className="text-sm font-medium">
                  {heroSection?.subtitle || 'Australian NDIS Technology Partner'}
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                About <span className="bg-gradient-to-r from-care-teal to-tech-blue bg-clip-text text-transparent">GetCare 360</span>
              </h1>
              
              <p className="text-xl text-white/80 mb-8 leading-relaxed">
                {heroSection?.content || "We're on a mission to help NDIS providers deliver better care through custom-built AI technology. Australian-owned, Australian-hosted, and 100% focused on the NDIS sector."}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={openBooking} className="bg-care-teal hover:bg-care-teal/90 text-white px-8 py-6 text-lg group shadow-lg shadow-care-teal/30">
                  <Play className="mr-2 group-hover:scale-110 transition-transform" size={20} />
                  Book a Demo
                </Button>
                <Button onClick={openContact} variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Contact Us
                </Button>
              </div>
            </div>

            {/* Trust Stats */}
            <div className="grid grid-cols-2 gap-4">
              {trustStats.map((stat, index) => {
                const Icon = stat.icon;
                const isActive = activeValue === index;
                return (
                  <div 
                    key={index}
                    className={`p-6 rounded-2xl backdrop-blur-sm transition-all duration-500 ${
                      isActive 
                        ? 'bg-white/15 border-2 border-care-teal/50 scale-105' 
                        : 'bg-white/5 border border-white/10'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl mb-4 flex items-center justify-center transition-all ${
                      isActive ? 'bg-care-teal' : 'bg-white/10'
                    }`}>
                      <Icon className="text-white" size={24} />
                    </div>
                    <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                      <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                    </div>
                    <p className="text-white/60 text-sm">{stat.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-white border-b border-border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8">
            {[
              { icon: Shield, text: 'NDIS Registered Provider' },
              { icon: MapPin, text: 'Australian Owned & Operated' },
              { icon: Lock, text: '100% Data Ownership' },
              { icon: Cloud, text: 'Australian Cloud Hosted' },
              { icon: Award, text: 'ISO 27001 Compliant' }
            ].map((badge, index) => (
              <div key={index} className="flex items-center gap-2 text-text-secondary hover:text-tech-blue transition-colors">
                <badge.icon size={20} className="text-care-teal" />
                <span className="text-sm font-medium">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center px-3 py-1 bg-tech-blue/10 rounded-full text-tech-blue text-sm font-medium mb-4">
                Our Story
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-6">
                Built by Australians, <span className="text-tech-blue">for Australians</span>
              </h2>
              <div className="space-y-4 text-text-secondary leading-relaxed">
                {storySection?.content ? (
                  storySection.content.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} dangerouslySetInnerHTML={{ __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-primary-navy">$1</strong>') }} />
                  ))
                ) : (
                  <>
                    <p>
                      GetCare 360 was born from a simple observation: <strong className="text-primary-navy">NDIS providers were struggling with generic software</strong> that wasn't built for the complexities of disability support services.
                    </p>
                    <p>
                      Our founders, with backgrounds in healthcare technology and NDIS service delivery, set out to build something different – a <strong className="text-primary-navy">custom platform that adapts to each provider</strong>, not the other way around.
                    </p>
                    <p>
                      Today, we're proud to support over <strong className="text-primary-navy">100 NDIS providers across Australia</strong>, helping them deliver better care while reducing administrative burden.
                    </p>
                  </>
                )}
              </div>
              
              <div className="mt-8 flex items-center gap-6">
                <div className="flex -space-x-3">
                  {[1,2,3,4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-care-teal to-tech-blue border-2 border-white flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{i}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-text-secondary">Join <strong className="text-tech-blue">100+ providers</strong> using GetCare</p>
              </div>
            </div>
            
            <div className="relative">
              {storySection?.image_url ? (
                <img 
                  src={storySection.image_url}
                  alt="Our Team"
                  className="rounded-2xl shadow-2xl"
                />
              ) : (
                <div className="bg-gradient-to-br from-primary-navy to-tech-blue rounded-2xl p-8 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                  <div className="relative z-10">
                    <h3 className="text-2xl font-bold mb-6">Why GetCare?</h3>
                    <ul className="space-y-4">
                      {[
                        'Custom-built for your workflow',
                        'No per-user fees - own your platform',
                        'AI-powered compliance automation',
                        'Australian support team',
                        'NDIS-specific features'
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-care-teal/30 flex items-center justify-center">
                            <Check className="text-care-teal" size={14} />
                          </div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl border border-border-line">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-care-teal/10 flex items-center justify-center">
                    <TrendingUp className="text-care-teal" size={24} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary-navy">60%</p>
                    <p className="text-xs text-text-secondary">Admin time saved</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-4">
              Our <span className="text-tech-blue">Core Values</span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Everything we do is guided by these principles
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(valuesSection?.items?.length > 0 ? valuesSection.items : coreValues).map((value, index) => {
              const Icon = iconMap[value.icon] || coreValues[index]?.icon || Award;
              return (
                <Card key={index} className="border border-border-line hover:border-tech-blue hover:-translate-y-2 transition-all duration-300 group bg-white">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-tech-blue/10 to-care-teal/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="text-tech-blue" size={26} />
                    </div>
                    <h3 className="text-lg font-bold text-primary-navy mb-2 group-hover:text-tech-blue transition-colors">
                      {value.title}
                    </h3>
                    <p className="text-text-secondary text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Not Generic SaaS */}
      <section className="py-20 bg-gradient-to-br from-primary-navy via-[#0d2847] to-tech-blue text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-96 h-96 bg-care-teal/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {differentiatorsSection?.title || 'Why Not Generic SaaS?'}
            </h2>
            <p className="text-xl text-white/80">
              {differentiatorsSection?.subtitle || 'Traditional software forces you to adapt. We build software that adapts to you.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(differentiatorsSection?.items?.length > 0 ? differentiatorsSection.items : [
              { icon: 'Lock', title: 'Own Your Software', description: 'No vendor lock-in. Your platform, your data, your future.' },
              { icon: 'Users', title: 'No Per-User Fees', description: 'Add unlimited staff without increasing your software costs.' },
              { icon: 'Brain', title: 'AI-Powered', description: 'Intelligent compliance monitoring and automated reporting.' },
              { icon: 'Target', title: 'Custom Workflows', description: 'Built around how YOUR organisation actually operates.' },
              { icon: 'Shield', title: 'Australian Hosted', description: 'Your data stays in Australia, compliant with local regulations.' },
              { icon: 'Zap', title: 'Fast Implementation', description: 'Go live in 2-6 weeks with full training and support.' }
            ]).map((item, index) => {
              const Icon = iconMap[item.icon] || Zap;
              return (
                <Card key={index} className="bg-white/10 border-white/20 backdrop-blur-sm hover:bg-white/15 transition-all group">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-care-teal/20 flex items-center justify-center mb-4 group-hover:bg-care-teal/30 transition-colors">
                      <Icon className="text-care-teal" size={24} />
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-white">{item.title}</h3>
                    <p className="text-white/70 text-sm">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-16 text-center">
            <p className="text-2xl font-semibold mb-2">You don't adapt to software.</p>
            <p className="text-2xl font-semibold text-care-teal">Your software adapts to you.</p>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      {techStackSection && techStackSection.items && techStackSection.items.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-4 py-2 bg-tech-blue/10 rounded-full border border-tech-blue/20 mb-6">
                <Zap className="text-tech-blue mr-2" size={20} />
                <span className="text-sm font-medium text-tech-blue">Enterprise-Grade Technology</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-4">
                {techStackSection.title}
              </h2>
              {techStackSection.subtitle && (
                <p className="text-text-secondary max-w-3xl mx-auto">
                  {techStackSection.subtitle}
                </p>
              )}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {techStackSection.items.map((tech, index) => {
                const Icon = iconMap[tech.icon] || Monitor;
                return (
                  <Card key={index} className="border border-border-line hover:border-tech-blue hover:shadow-lg transition-all">
                    <CardContent className="p-8">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="w-14 h-14 rounded-xl bg-tech-blue/10 flex items-center justify-center">
                          <Icon className="text-tech-blue" size={28} />
                        </div>
                        <h3 className="text-xl font-bold text-primary-navy">{tech.category}</h3>
                      </div>
                      <div className="space-y-3">
                        {tech.technologies?.map((technology, idx) => (
                          <div key={idx} className="flex items-center space-x-2">
                            <CheckCircle className="text-care-teal flex-shrink-0" size={16} />
                            <span className="text-text-secondary">{technology}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-surface">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-4">
                Trusted by <span className="text-tech-blue">NDIS Providers</span>
              </h2>
              <p className="text-text-secondary">See what our customers have to say</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.slice(0, 3).map((testimonial, index) => (
                <Card key={testimonial.id || index} className="border border-border-line hover:border-tech-blue hover:-translate-y-2 transition-all duration-300 bg-white group">
                  <CardContent className="p-8">
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating || 5)].map((_, i) => (
                        <Star key={i} className="text-heritage-orange fill-heritage-orange group-hover:scale-110 transition-transform" size={20} style={{ transitionDelay: `${i * 50}ms` }} />
                      ))}
                    </div>
                    <p className="text-text-secondary mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                    <div className="border-t border-border-line pt-4">
                      <div className="font-semibold text-primary-navy">{testimonial.name}</div>
                      <div className="text-sm text-text-secondary">{testimonial.role}</div>
                      <div className="text-sm text-tech-blue">{testimonial.company}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-4">
                Frequently Asked <span className="text-tech-blue">Questions</span>
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={faq.id || index} 
                  value={`item-${faq.id || index}`}
                  className="border-2 border-border-line rounded-xl px-6 hover:border-tech-blue transition-colors bg-surface"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-primary-navy hover:text-tech-blue py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-text-secondary leading-relaxed pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* Partnership Program */}
      {partnersSection && partnersSection.items && partnersSection.items.length > 0 && (
        <section className="py-20 bg-surface">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-4 py-2 bg-tech-blue/10 rounded-full border border-tech-blue/20 mb-6">
                <Briefcase className="text-tech-blue mr-2" size={18} />
                <span className="text-sm font-medium text-tech-blue">Partner With Us</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-4">
                {partnersSection.title}
              </h2>
              {partnersSection.subtitle && (
                <p className="text-text-secondary max-w-3xl mx-auto">
                  {partnersSection.subtitle}
                </p>
              )}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {partnersSection.items.map((partner, index) => {
                const Icon = iconMap[partner.icon] || Briefcase;
                return (
                  <Card key={index} className="border border-border-line hover:border-tech-blue hover:-translate-y-2 transition-all duration-300 group bg-white">
                    <CardContent className="p-6">
                      <div className="w-14 h-14 rounded-2xl bg-tech-blue/10 flex items-center justify-center mb-4 group-hover:bg-tech-blue group-hover:scale-110 transition-all">
                        <Icon className="text-tech-blue group-hover:text-white transition-colors" size={26} />
                      </div>
                      <h3 className="text-lg font-bold text-primary-navy mb-1 group-hover:text-tech-blue transition-colors">
                        {partner.type}
                      </h3>
                      <p className="text-care-teal font-semibold text-sm mb-3">{partner.commission}</p>
                      <ul className="space-y-2">
                        {partner.benefits?.slice(0, 3).map((benefit, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                            <CheckCircle className="text-care-teal flex-shrink-0 mt-0.5" size={14} />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="mt-12 text-center">
              <Button onClick={openContact} className="bg-tech-blue hover:bg-tech-blue/90 text-white px-8 py-6 text-lg group">
                Become a Partner
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-navy via-tech-blue to-purple-900 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-care-teal/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }}></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-6">
            <Sparkles className="text-care-teal mr-2" size={16} />
            <span className="text-sm font-medium">Australian Made & Supported</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Work <span className="text-care-teal">With Us?</span>
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Let's discuss how we can build the perfect NDIS platform for your organisation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openBooking} className="bg-care-teal hover:bg-care-teal/90 text-white px-10 py-6 text-lg group shadow-lg shadow-care-teal/30">
              <Play className="mr-2 group-hover:scale-110 transition-transform" size={20} />
              Book a Demo
            </Button>
            <Button onClick={openContact} variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg">
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
